import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import { useRole } from '../../lib/role.tsx';
import { MODULES } from '../../lib/modules.js';
import { canEdit } from '../../lib/role.tsx';
import {
  type AccountHistoryEvent,
  ACCOUNT_HISTORY_EVENT_LABELS,
  formatIban,
  validateGeIban,
} from '@mnp/shared';

const mod = MODULES.find((m) => m.slug === 'accounts')!;

interface Account {
  id: string;
  label: string;
  bankName: string | null;
  bankCode?: string | null;
  accountNumber: string;
  holderName: string;
  currency: string;
  requisitesText: string;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string;
}

interface AccountHistory {
  id: string;
  accountId: string;
  event: AccountHistoryEvent | string;
  actorChatId?: string | null;
  createdAt: string;
}

interface AccountFormValues {
  label: string;
  bankName: string;
  accountNumber: string;
  holderName: string;
  requisitesText: string;
  currency: string;
}

type DrawerState =
  | { mode: 'create'; account?: undefined }
  | { mode: 'edit'; account: Account }
  | null;

const EMPTY_FORM: AccountFormValues = {
  label: '',
  bankName: '',
  accountNumber: '',
  holderName: '',
  requisitesText: '',
  currency: 'GEL',
};

function bankKey(account: Pick<Account, 'bankCode' | 'bankName'>): string {
  return (account.bankCode || account.bankName || 'other')
    .toLowerCase()
    .replace(/bank/g, '')
    .replace(/[^a-z0-9а-яё]+/gi, '-')
    .replace(/^-|-$/g, '') || 'other';
}

function bankLabel(account: Pick<Account, 'bankCode' | 'bankName'>): string {
  return account.bankCode?.toUpperCase() || account.bankName || 'Другой';
}

function tail(value: string): string {
  return value.slice(-4);
}

function toFormValues(account: Account): AccountFormValues {
  return {
    label: account.label,
    bankName: account.bankName ?? '',
    accountNumber: account.accountNumber,
    holderName: account.holderName,
    requisitesText: account.requisitesText,
    currency: account.currency,
  };
}

function eventLabel(event: string): string {
  return ACCOUNT_HISTORY_EVENT_LABELS[event as AccountHistoryEvent] ?? event;
}


export function AccountsLanding() {
  const role = useRole();
  const qc = useQueryClient();
  const canWrite = canEdit(role, mod);

  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [confirmActivate, setConfirmActivate] = useState<Account | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Account | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState('all');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: accounts = [], isLoading } = useQuery<Account[]>({
    queryKey: ['admin-accounts'],
    queryFn: () => api<Account[]>('/admin/accounts'),
  });

  const activeAccount = accounts.find((a) => a.isActive && a.deletedAt === null) ?? null;
  const visibleAccounts = accounts.filter((a) => a.deletedAt === null);

  const bankOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const account of visibleAccounts) {
      seen.set(bankKey(account), bankLabel(account));
    }
    return Array.from(seen, ([key, label]) => ({ key, label }));
  }, [visibleAccounts]);

  const cardAccounts = visibleAccounts.filter((account) => {
    if (account.isActive) return false;
    return selectedBank === 'all' || bankKey(account) === selectedBank;
  });

  const createMutation = useMutation({
    mutationFn: (values: AccountFormValues) =>
      api('/admin/accounts', { method: 'POST', body: JSON.stringify(values) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-accounts'] });
      setDrawer(null);
      setFormError(null);
    },
    onError: (err) => setFormError(errorText(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: AccountFormValues }) =>
      api(`/admin/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(values) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-accounts'] });
      setDrawer(null);
      setFormError(null);
    },
    onError: (err) => setFormError(errorText(err)),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api(`/admin/accounts/${id}/activate`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-accounts'] });
      setConfirmActivate(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/admin/accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-accounts'] });
      setConfirmDelete(null);
    },
  });

  const { data: history = [] } = useQuery<AccountHistory[]>({
    queryKey: ['admin-account-history'],
    queryFn: () => api<AccountHistory[]>('/admin/accounts/history'),
    enabled: historyOpen,
  });

  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  function openCreate() {
    setDrawer({ mode: 'create' });
    setFormError(null);
  }

  function openEdit(account: Account) {
    setDrawer({ mode: 'edit', account });
    setFormError(null);
  }

  return (
    <div data-testid="accounts-landing" className="content accounts-page">
      <div className="accounts-head">
        <div>
          <h1>Счета</h1>
          <p>Реквизиты для дистанционной оплаты заказов</p>
        </div>
        {canWrite && (
          <button data-testid="add-account-btn" className="btn-admin primary" onClick={openCreate}>
            Добавить счёт
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="panel">Загрузка...</div>
      ) : visibleAccounts.length === 0 ? (
        <div className="accounts-empty">
          <h2>Нет активного счёта</h2>
          <p>Добавьте реквизиты, чтобы клиенты могли оплачивать дистанционно.</p>
          {canWrite && (
            <button className="btn-admin primary" onClick={openCreate}>
              Добавить счёт
            </button>
          )}
        </div>
      ) : (
        <>
          {activeAccount ? (
            <ActiveAccountHero account={activeAccount} canWrite={canWrite} onEdit={() => openEdit(activeAccount)} />
          ) : (
            <div className="accounts-empty compact">
              <h2>Нет активного счёта</h2>
              <p>Выберите счёт из списка и сделайте его активным.</p>
            </div>
          )}

          <BankSegment
            banks={bankOptions}
            selected={selectedBank}
            onSelect={setSelectedBank}
          />

          <div className="accounts-grid">
            {cardAccounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                canWrite={canWrite}
                onActivate={() => setConfirmActivate(account)}
                onEdit={() => openEdit(account)}
                onArchive={() => setConfirmDelete(account)}
              />
            ))}
            {cardAccounts.length === 0 && (
              <div className="accounts-empty compact">Нет счетов в выбранном банке</div>
            )}
          </div>

          <div className="accounts-history-cta">
            <button
              data-testid="history-drawer-btn"
              className="btn-admin"
              onClick={() => setHistoryOpen(true)}
            >
              История переключений
            </button>
          </div>
        </>
      )}

      {drawer && (
        <AccountDrawer
          mode={drawer.mode}
          initial={drawer.mode === 'edit' ? toFormValues(drawer.account) : EMPTY_FORM}
          isPending={createMutation.isPending || updateMutation.isPending}
          errorMsg={formError}
          onClose={() => setDrawer(null)}
          onSubmit={(values) => {
            if (drawer.mode === 'create') {
              createMutation.mutate(values);
            } else {
              updateMutation.mutate({ id: drawer.account.id, values });
            }
          }}
        />
      )}

      {confirmActivate && (
        <ActivateConfirm
          activeAccount={activeAccount}
          candidate={confirmActivate}
          isPending={activateMutation.isPending}
          onClose={() => setConfirmActivate(null)}
          onConfirm={() => activateMutation.mutate(confirmActivate.id)}
        />
      )}

      {confirmDelete && (
        <Modal title="Архивировать счёт?" onClose={() => setConfirmDelete(null)}>
          <p className="accounts-dialog-text">
            Будет архивирован: <strong>{confirmDelete.label}</strong>
          </p>
          <p className="accounts-warning">Активный счёт архивировать нельзя.</p>
          <div className="accounts-dialog-actions">
            <button className="btn-admin" onClick={() => setConfirmDelete(null)}>
              Отмена
            </button>
            <button
              data-testid="confirm-delete-btn"
              className="btn-admin"
              disabled={deleteMutation.isPending || confirmDelete.isActive}
              onClick={() => deleteMutation.mutate(confirmDelete.id)}
            >
              {deleteMutation.isPending ? 'Архивирую...' : 'Архивировать'}
            </button>
          </div>
        </Modal>
      )}

      {historyOpen && (
        <HistoryDrawer history={sortedHistory} onClose={() => setHistoryOpen(false)} />
      )}
    </div>
  );
}

function ActiveAccountHero({
  account,
  canWrite,
  onEdit,
}: {
  account: Account;
  canWrite: boolean;
  onEdit: () => void;
}) {
  return (
    <section data-testid="active-account-hero" className="active-account-hero">
      <div>
        <span className="accounts-kicker">Активный счёт</span>
        <h2>{account.label}</h2>
        <p>{account.bankName ?? 'Банк не указан'}</p>
      </div>
      <div className="active-account-details">
        <span>{formatIban(account.accountNumber)}</span>
        <span>{account.holderName}</span>
      </div>
      {canWrite && (
        <button data-testid={`edit-btn-${account.id}`} className="btn-admin" onClick={onEdit}>
          Редактировать
        </button>
      )}
    </section>
  );
}

function BankSegment({
  banks,
  selected,
  onSelect,
}: {
  banks: Array<{ key: string; label: string }>;
  selected: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div data-testid="bank-segment" className="bank-segment" role="tablist" aria-label="Фильтр банков">
      <button
        data-testid="bank-pill-all"
        className={selected === 'all' ? 'active' : ''}
        onClick={() => onSelect('all')}
      >
        Все
      </button>
      {banks.map((bank) => (
        <button
          key={bank.key}
          data-testid={`bank-pill-${bank.key}`}
          className={selected === bank.key ? 'active' : ''}
          onClick={() => onSelect(bank.key)}
        >
          {bank.label}
        </button>
      ))}
      <button data-testid="bank-pill-crypto" aria-disabled="true" disabled>
        Крипта <span>Скоро</span>
      </button>
    </div>
  );
}

function AccountCard({
  account,
  canWrite,
  onActivate,
  onEdit,
  onArchive,
}: {
  account: Account;
  canWrite: boolean;
  onActivate: () => void;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <article data-testid={`account-card-${account.id}`} className="account-card">
      <div className="account-card-top">
        <div>
          <h3>{account.label}</h3>
          <p>{account.bankName ?? 'Банк не указан'}</p>
        </div>
        <span>{account.currency}</span>
      </div>
      <div className="account-card-number">{formatIban(account.accountNumber)}</div>
      <div className="account-card-holder">{account.holderName}</div>
      {canWrite && (
        <div className="account-card-actions">
          <button data-testid={`activate-btn-${account.id}`} className="btn-admin sm primary" onClick={onActivate}>
            Сделать активным
          </button>
          <button data-testid={`edit-btn-${account.id}`} className="btn-admin sm" onClick={onEdit}>
            Редактировать
          </button>
          <button
            data-testid={`delete-btn-${account.id}`}
            className="btn-admin sm"
            disabled={account.isActive}
            title={account.isActive ? 'Нельзя удалить активный счёт' : undefined}
            onClick={onArchive}
          >
            Архивировать
          </button>
        </div>
      )}
    </article>
  );
}

function AccountDrawer({
  mode,
  initial,
  isPending,
  errorMsg,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  initial: AccountFormValues;
  isPending: boolean;
  errorMsg: string | null;
  onClose: () => void;
  onSubmit: (values: AccountFormValues) => void;
}) {
  const [form, setForm] = useState<AccountFormValues>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof AccountFormValues, string>>>({});
  const [requisitesTouched, setRequisitesTouched] = useState(mode === 'edit');

  function patch<K extends keyof AccountFormValues>(key: K, value: AccountFormValues[K]) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key !== 'requisitesText' && !requisitesTouched) {
        next.requisitesText = composeRequisites(next);
      }
      return next;
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit({
      ...form,
      accountNumber: form.accountNumber.replace(/\s+/g, '').toUpperCase(),
      bankName: form.bankName.trim(),
      label: form.label.trim(),
      holderName: form.holderName.trim(),
      requisitesText: form.requisitesText.trim(),
    });
  }

  return (
    <div className="account-drawer-backdrop" role="presentation">
      <aside data-testid="account-drawer" className="account-drawer" aria-label="Форма счёта">
        <div className="account-drawer-head">
          <h2>{mode === 'create' ? 'Новый счёт' : 'Редактировать счёт'}</h2>
          <button className="btn-admin" onClick={onClose} type="button">Закрыть</button>
        </div>
        <form onSubmit={handleSubmit} className="account-drawer-form">
          <DrawerField label="Название" id="account-label" error={errors.label} errorId="field-error-label">
            <input id="account-label" value={form.label} onChange={(e) => patch('label', e.target.value)} />
          </DrawerField>
          <DrawerField label="Банк" id="account-bankName" error={errors.bankName} errorId="field-error-bankName">
            <input id="account-bankName" value={form.bankName} onChange={(e) => patch('bankName', e.target.value)} />
          </DrawerField>
          <DrawerField
            label="IBAN / номер счёта"
            id="account-accountNumber"
            error={errors.accountNumber}
            errorId="field-error-accountNumber"
          >
            <input
              id="account-accountNumber"
              value={form.accountNumber}
              onChange={(e) => patch('accountNumber', e.target.value)}
              placeholder="GE00 TB00 0000 0000 0000 00"
            />
          </DrawerField>
          <DrawerField label="Получатель" id="account-holderName" error={errors.holderName} errorId="field-error-holderName">
            <input id="account-holderName" value={form.holderName} onChange={(e) => patch('holderName', e.target.value)} />
          </DrawerField>
          <DrawerField
            label="Реквизиты"
            id="account-requisitesText"
            error={errors.requisitesText}
            errorId="field-error-requisitesText"
          >
            <textarea
              id="account-requisitesText"
              value={form.requisitesText}
              rows={5}
              onChange={(e) => {
                setRequisitesTouched(true);
                patch('requisitesText', e.target.value);
              }}
            />
          </DrawerField>
          {errorMsg && <div className="accounts-warning">{errorMsg}</div>}
          <div className="accounts-dialog-actions">
            <button type="button" className="btn-admin" onClick={onClose} disabled={isPending}>
              Отмена
            </button>
            <button data-testid="account-drawer-submit" type="submit" className="btn-admin primary" disabled={isPending}>
              {isPending ? 'Сохраняю...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

function DrawerField({
  label,
  id,
  error,
  errorId,
  children,
}: {
  label: string;
  id: string;
  error?: string;
  errorId: string;
  children: ReactNode;
}) {
  return (
    <div className="drawer-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error && <div data-testid={errorId} className="field-error">{error}</div>}
    </div>
  );
}

function ActivateConfirm({
  activeAccount,
  candidate,
  isPending,
  onClose,
  onConfirm,
}: {
  activeAccount: Account | null;
  candidate: Account;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal title="Подтвердить переключение" onClose={onClose} testId="activate-confirm-modal">
      <div className="activate-compare">
        {activeAccount && (
          <div>
            <span>Сейчас активен</span>
            <strong>{activeAccount.label}</strong>
            <p>{activeAccount.bankName ?? 'Банк не указан'} · {tail(activeAccount.accountNumber)}</p>
          </div>
        )}
        <div>
          <span>Будет активен</span>
          <strong>{candidate.label}</strong>
          <p>{candidate.bankName ?? 'Банк не указан'} · {tail(candidate.accountNumber)}</p>
        </div>
      </div>
      <div className="accounts-dialog-actions">
        <button className="btn-admin" onClick={onClose}>Отмена</button>
        <button
          data-testid="confirm-activate-btn"
          className="btn-admin primary"
          disabled={isPending}
          onClick={onConfirm}
        >
          {isPending ? 'Переключаю...' : 'Подтвердить переключение'}
        </button>
      </div>
    </Modal>
  );
}

function HistoryDrawer({ history, onClose }: { history: AccountHistory[]; onClose: () => void }) {
  return (
    <div className="account-drawer-backdrop" role="presentation">
      <aside data-testid="history-drawer" className="account-drawer history-drawer" aria-label="История переключений">
        <div className="account-drawer-head">
          <h2>История переключений</h2>
          <button className="btn-admin" onClick={onClose} type="button">Закрыть</button>
        </div>
        {history.length === 0 ? (
          <div className="muted">Нет записей</div>
        ) : (
          <div className="history-list">
            {history.map((item) => (
              <div key={item.id} data-testid={`history-row-${item.id}`} className="history-row">
                <strong>{eventLabel(item.event)}</strong>
                <span>{new Date(item.createdAt).toLocaleString('ru-RU')}</span>
                {item.actorChatId && <span>{`chatId: ${item.actorChatId}`}</span>}
              </div>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

function Modal({
  title,
  testId,
  onClose,
  children,
}: {
  title: string;
  testId?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="modal-backdrop" role="presentation">
      <div data-testid={testId} className="modal-card" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn-admin sm" onClick={onClose}>Закрыть</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function composeRequisites(values: AccountFormValues): string {
  return [
    values.bankName && `Банк: ${values.bankName}`,
    values.accountNumber && `IBAN: ${formatIban(values.accountNumber)}`,
    values.holderName && `Получатель: ${values.holderName}`,
  ].filter(Boolean).join('\n');
}

function validateForm(values: AccountFormValues): Partial<Record<keyof AccountFormValues, string>> {
  const errors: Partial<Record<keyof AccountFormValues, string>> = {};
  if (!values.label.trim()) errors.label = 'Укажите название';
  if (!values.bankName.trim()) errors.bankName = 'Укажите банк';
  if (!values.accountNumber.trim()) {
    errors.accountNumber = 'Укажите IBAN';
  } else if (!validateGeIban(values.accountNumber).ok) {
    errors.accountNumber = 'Проверьте грузинский IBAN';
  }
  if (!values.holderName.trim()) errors.holderName = 'Укажите получателя';
  if (!values.requisitesText.trim()) errors.requisitesText = 'Укажите реквизиты';
  return errors;
}

function errorText(err: unknown): string {
  return err instanceof ApiError ? (err.body?.message ?? err.message) : String(err);
}
